import React, { useState } from "react";
import { Check, RotateCcw } from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";

const photoUrl = (p) => {
  const token = localStorage.getItem("auth_token");
  return `${API}/completion-photos/${p.storage_path}${token ? `?auth=${token}` : ""}`;
};

const STATUS_STYLE = {
  open: "bg-amber-100 text-amber-800 border-amber-300",
  complete: "bg-emerald-100 text-emerald-800 border-emerald-300",
  closed_by_gc: "bg-gray-100 text-gray-500 border-gray-300",
};

/** Cluster 11 — Punch view: one row per punch item. */
export default function PunchView({ items, onChanged }) {
  const [saving, setSaving] = useState("");

  const patch = async (item, body) => {
    setSaving(item.id);
    try {
      await api.patch(`/punch-items/${item.id}`, body);
      if ("material_needed" in body) toast.success("Material noted");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the punch item");
    }
    setSaving("");
  };

  if (!items.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg py-10 text-center text-sm text-gray-500"
           data-testid="punch-view-empty">
        No punch items yet — use “Upload punchlist” to import the GC's list.
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="punch-view">
      <table className="w-full text-[12px]">
        <thead className="bg-gray-50 text-gray-600 sticky top-0">
          <tr>
            <th className="px-2 py-2 text-left">Room</th>
            <th className="px-2 py-2 text-left">Scope</th>
            <th className="px-2 py-2 text-left">Description</th>
            <th className="px-2 py-2 text-left">GC note</th>
            <th className="px-2 py-2 text-left">Material needed</th>
            <th className="px-2 py-2 text-left">Photo</th>
            <th className="px-2 py-2 text-left">Status</th>
            <th className="px-2 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t border-gray-100 align-top" data-testid={`punch-row-${it.id}`}>
              <td className="px-2 py-2 whitespace-nowrap">
                {[it.building, it.floor && `F${it.floor}`, it.room_number].filter(Boolean).join(" · ") || "—"}
              </td>
              <td className="px-2 py-2 font-mono text-purple-700 whitespace-nowrap">{it.scope_type || "—"}</td>
              <td className="px-2 py-2 max-w-[320px]">{it.description}</td>
              <td className="px-2 py-2 max-w-[220px] text-gray-600">{it.note || "—"}</td>
              <td className="px-2 py-2">
                <input
                  defaultValue={it.material_needed || ""}
                  onBlur={(e) => {
                    if ((e.target.value || "") !== (it.material_needed || "")) {
                      patch(it, { material_needed: e.target.value });
                    }
                  }}
                  placeholder="free text"
                  className="h-8 w-40 text-[12px] border border-gray-300 rounded px-2"
                  data-testid={`punch-material-${it.id}`}
                />
              </td>
              <td className="px-2 py-2">
                {(it.photos || []).length > 0 ? (
                  <a href={photoUrl(it.photos[0])} target="_blank" rel="noopener noreferrer"
                     data-testid={`punch-photo-${it.id}`}>
                    <img src={photoUrl(it.photos[0])} alt="punch" className="w-10 h-10 object-cover rounded border border-gray-200" />
                  </a>
                ) : <span className="text-gray-300">—</span>}
              </td>
              <td className="px-2 py-2">
                <span className={`text-[10px] uppercase font-bold border rounded px-1.5 py-0.5 whitespace-nowrap ${STATUS_STYLE[it.status] || STATUS_STYLE.open}`}
                      data-testid={`punch-status-${it.id}`}>
                  {it.status === "closed_by_gc" ? "closed by GC" : it.status}
                </span>
              </td>
              <td className="px-2 py-2 text-right">
                {it.status !== "closed_by_gc" && (
                  <button
                    type="button"
                    disabled={saving === it.id}
                    onClick={() => patch(it, { status: it.status === "complete" ? "open" : "complete" })}
                    className="text-[11px] inline-flex items-center gap-1 border border-gray-300 rounded px-2 py-1 hover:bg-gray-50"
                    data-testid={`punch-toggle-${it.id}`}
                  >
                    {it.status === "complete"
                      ? <><RotateCcw className="w-3 h-3" /> Reopen</>
                      : <><Check className="w-3 h-3" /> Complete</>}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
